import React, { useRef, useState } from 'react';
import { Button, Typography, Form } from 'antd';
import { CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';

const { Text } = Typography;

interface AIDrawerAlertMessageBoxProps {
  showMismatchAlert: boolean;
  fullNamePath?: (string | number)[];
  onAccept?: () => void;
  validationResult?: any;
  showSuccessMessage?: boolean;
  onClose?: () => void;
}

const AIDrawerAlertMessageBox: React.FC<AIDrawerAlertMessageBoxProps> = ({
  showMismatchAlert,
  fullNamePath,
  onAccept,
  validationResult,
  showSuccessMessage,
  onClose,
}) => {
  const [showRationaleTooltip, setShowRationaleTooltip] = useState(false);
  const rationaleButtonRef = useRef<HTMLAnchorElement>(null);
  const form = Form.useFormInstance();

  const result = validationResult?.classification?.result;
  const htsNumber = result?.htsNumber;
  const reasoning = result?.reasoning;

  const handleAccept = () => {
    if (htsNumber && fullNamePath) {
      form.setFieldValue([...fullNamePath, 'tariffs'], [{ htsNumber }]);
    }
    if (onAccept) {
      onAccept();
    }
  };

  if (showSuccessMessage) {
    return (
      <div
        style={{
          display: 'flex',
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: 6,
          padding: '12px 16px',
          maxWidth: 1000,
          color: '#000',
          fontSize: 14,
          position: 'relative',
        }}
      >
        <CheckCircleFilled
          style={{
            color: '#52c41a',
            fontSize: 18,
            marginRight: 12,
            marginTop: 4,
          }}
        />
        <div style={{ flex: 1 }}>
          <Text style={{ display: 'block', marginBottom: 4 }}>
            Your HTS code and calculated duties have been updated.
          </Text>
          <Text>
            Disclaimer: Classifications are generated by artificial intelligence
            based on your inputs and should be reviewed for accuracy. You are
            responsible for ensuring your goods are properly classified.
          </Text>
        </div>
        {onClose && (
          <Button
            type="text"
            size="small"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: 0,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </Button>
        )}
      </div>
    );
  }

  if (showMismatchAlert) {
    return (
      <div
        style={{
          backgroundColor: '#fff1f0',
          border: '1px solid #ffa39e',
          padding: '12px 16px',
          borderRadius: 6,
          display: 'flex',
          gap: 12,
          maxWidth: 1000,
          marginBottom: 24,
          position: 'relative',
        }}
      >
        <CloseCircleFilled
          style={{
            fontSize: 16,
            color: '#cf1322',
            marginTop: 4,
            flexShrink: 0,
          }}
        />
        <div style={{ color: '#000', fontSize: 14, lineHeight: '20px' }}>
          <div style={{ marginBottom: 8 }}>
            The HTS code doesn't match your product details, based on AI
            recommendations. To minimize potential customs delays, choose one:
          </div>
          <ul style={{ paddingLeft: 20, margin: 0, marginBottom: 12 }}>
            <li>
              Use recommended CBP description and HTS Code:{' '}
              <strong>{htsNumber}</strong>.{' '}
              <a
                href="#"
                ref={rationaleButtonRef}
                style={{ color: '#d63384' }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowRationaleTooltip((prev) => !prev);
                }}
              >
                View rationale
              </a>
            </li>
            <li>Adjust CBP description</li>
            <li>Keep current code (may face closer scrutiny at customs)</li>
          </ul>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Button onClick={handleAccept} type="default">
              Use recommended HTS code
            </Button>
            <Button type="default">Edit CBP description</Button>
            <Button type="default">Keep current code</Button>
          </div>

          {showRationaleTooltip && rationaleButtonRef.current && (
            <div
              style={{
                position: 'absolute',
                top: -160,
                left: rationaleButtonRef.current.offsetLeft,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: 6,
                padding: 16,
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                width: 360,
                zIndex: 10,
              }}
            >
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Rationale
              </Text>
              <Text style={{ fontSize: 13 }}>{reasoning}</Text>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#fff1f0',
        border: '1px solid #ffa39e',
        color: '#cf1322',
        padding: '12px 16px',
        borderRadius: 6,
        display: 'flex',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        marginBottom: 24,
        maxWidth: 1000,
      }}
    >
      <CloseCircleFilled
        style={{
          fontSize: 16,
          color: '#cf1322',
          marginRight: 8,
          marginTop: 2,
        }}
      />
      <div>
        The AI Classification Assistant needs more information to verify HTS
        code.
        <br />
        Answer the following questions.
      </div>
    </div>
  );
};

export default AIDrawerAlertMessageBox;
